<!--
  One banner, replacing six duplicated `.lw-notice` copies (refresh_plan.md
  3.1.6) and becoming the render target for errorMessage.vue's thirteen
  unstyled "Error: ..." surfaces in phase 2. `tone` is read straight off the
  feedback axis in state.js so the two vocabularies cannot drift apart.
-->
<template>
  <div class="lw-c-notice" :class="`lw-c-notice--${tone}`" :role="role">
    <div class="lw-c-notice__body">
      <!--
        Colour must never be the only signal (refresh_plan.md 3.1.6): danger
        and warning always render a word even when the caller passes no
        title, so a monochrome reader still learns the severity.
      -->
      <p v-if="displayTitle" class="lw-c-notice__title">
        {{ displayTitle }}
      </p>
      <div class="lw-c-notice__message">
        <slot />
      </div>
    </div>
    <div v-if="$slots.actions" class="lw-c-notice__actions">
      <slot name="actions" />
    </div>
    <!--
      A real button, never a `<span @click>`: this is what makes Enter and
      Space work for free and is what the a11y contract asserts on.
    -->
    <button v-if="dismissible" type="button" class="lw-c-notice__dismiss" @click="$emit('dismiss')">
      Dismiss
    </button>
  </div>
</template>

<script>
import { valuesFor, stateEntry } from './state';

const FEEDBACK_TONES = valuesFor('feedback');

export default {
  name: 'LwNotice',
  props: {
    tone: {
      type: String,
      default: 'info',
      validator: value => FEEDBACK_TONES.includes(value),
    },
    title: {
      type: String,
      default: null,
    },
    dismissible: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['dismiss'],
  computed: {
    // The plan's split: an error interrupts (role="alert"), a confirmation
    // does not (role="status").
    role() {
      return this.tone === 'danger' ? 'alert' : 'status';
    },
    displayTitle() {
      if (this.title) return this.title;
      if (this.tone === 'danger' || this.tone === 'warning') {
        const entry = stateEntry('feedback', this.tone);
        return entry ? entry.label : null;
      }
      return null;
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../../sass/mixins.generated';

.lw-c-notice {
  display: flex;
  align-items: flex-start;
  gap: var(--lw-space-3);
  padding: var(--lw-space-4);
  border-radius: var(--lw-radius-sm);
  border: 1px solid var(--lw-elev-0-line);
  // Elev-0 per the contract: flat, border only. A notice sits in the page,
  // not above it, so no shadow is applied even though the token exists.
  box-shadow: var(--lw-elev-0-shadow);
  // The token file has no border-width scale (only border colour tokens), so
  // the left edge rule's thickness is a raw geometry value, same exception
  // class as a 1px hairline.
  border-left-width: 3px;
  font: var(--lw-type-body-sm);
}

.lw-c-notice__body {
  flex: 1;
  min-width: 0;
}

.lw-c-notice__title {
  margin: 0 0 var(--lw-space-1);
  font: var(--lw-type-caption-strong);
}

.lw-c-notice__message {
  margin: 0;
}

.lw-c-notice__actions {
  flex: none;
  display: flex;
  align-items: center;
  gap: var(--lw-space-2);
}

.lw-c-notice__dismiss {
  flex: none;
  border: 0;
  background: transparent;
  color: inherit;
  font: var(--lw-type-caption-strong);
  cursor: pointer;
  padding: var(--lw-space-1) var(--lw-space-2);
  border-radius: var(--lw-radius-xs);
  opacity: 0.75;

  &:hover {
    opacity: 1;
  }

  @include lw-focus-visible;
}

// One modifier per feedback value in state.js. Enumerated rather than
// interpolated because CSS custom-property names cannot be built at
// runtime from a prop value.
.lw-c-notice--success {
  background: var(--lw-success-bg);
  color: var(--lw-success-fg);
  border-left-color: var(--lw-success-edge);
}

.lw-c-notice--warning {
  background: var(--lw-warning-bg);
  color: var(--lw-warning-fg);
  border-left-color: var(--lw-warning-edge);
}

.lw-c-notice--danger {
  background: var(--lw-danger-bg);
  color: var(--lw-danger-fg);
  border-left-color: var(--lw-danger-edge);
}

.lw-c-notice--info {
  background: var(--lw-info-bg);
  color: var(--lw-info-fg);
  border-left-color: var(--lw-info-edge);
}

.lw-c-notice--neutral {
  background: var(--lw-neutral-bg);
  color: var(--lw-neutral-fg);
  border-left-color: var(--lw-neutral-edge);
}
</style>
